import { checkAndSetIfArePreSelectedOffersAndServices, makeCustomizedOfferedServiceSelection, useBookingStore } from "@/stores/booking";
import { useBookingStepperStore } from "@/stores/booking-stepper";
import { usePostSaleStore } from "@/stores/post-sale";
import { computed, provide, ref, shallowRef, readonly, inject, reactive, isRef, toRef, watch } from "vue";
import {get as _get, has as _has} from 'lodash-es';
import { useDialogManagerStore } from "@/stores/dialog-manager";
import {useI18n} from "vue-i18n";

/**
 * 
 * @param {Ref} travelSolution 
 */
export function useTravelSolutionSelection(travelSolutionView, {isReturn = false, isCarnet = false, initialUseCustomizedOffers = false} = {}){

	const postSale = usePostSaleStore();
	const stepper = useBookingStepperStore();
	const booking = useBookingStore();
	const dialogManagerStore = useDialogManagerStore();
	const axios = inject('axios');
	const initialState = {
		selectedCustomizedOffers: null,
		selectedTrainGridOffers: null,
		useCustomizedOffers: initialUseCustomizedOffers
	};
	const state = reactive({ ...initialState });
	if(!travelSolutionView) travelSolutionView = shallowRef(null);
	else if(!isRef(travelSolutionView)) travelSolutionView = toRef(travelSolutionView);

	const selectedOffers = computed(() => {
		if(state.useCustomizedOffers){
			if(!state.selectedCustomizedOffers) return null;
			return Object.values(state.selectedCustomizedOffers).reduce((a, p) => {
				return Object.values(p).reduce((a1, p1) => {
					if(p1.offered) a1.push(...p1.offered);
					return a1;
				}, a);
			}, []);
		}else{
			if(!state.selectedTrainGridOffers) return null;
			return Object.values(state.selectedTrainGridOffers).reduce((a, p) => {
				if(p.offers) a.push(...p.offers);
				return a;
			}, []);
		}
	});

	const selectedOffersData = computed(() =>{
		if(!selectedOffers.value) return [];
		return selectedOffers.value.map((id) => {
			return _get(travelSolutionView.value, ['offeredServices', id]);
		}).filter(o => o); // filter missing data
	});

	/**
	 * Same of selectedOffersData but filtered only SEAT and BED services
	 * If we sell other services (SHOP, ...) this help to filter
	 */
	const selectedSeatOffersData = computed(() => {
		return selectedOffersData.value.filter(o => {
			return ['SEAT', 'BED'].includes(_get(booking.servicesMap, ['catalog_services', o.catalogServiceId, 'type']));
		});
	});

	/**
	 * Include choosen parameters for the selected offeredService
	 */
	const selectedOffersExtended = computed(() =>{
		// currently only customized offers have additional parameters
		if(state.useCustomizedOffers){
			if(!state.selectedCustomizedOffers) return null;
			return Object.values(state.selectedCustomizedOffers).reduce((a, p) => {
				return Object.values(p).reduce((a1, p1) => {
					// offered at 99% are always array of one element
					if(p1.offered){
						a1.push(...p1.offered.map(oId => {
							return {xmlId: oId, parameters: p1.parameters};
						}));
					}
					return a1;
				}, a);
			}, []);
		}else{
			return selectedOffers.value;
		}
	});

	const canRequestSeatSelection = computed(() => {
		if(!selectedSeatOffersData.value) return false;
		
		let hasDifferentCatalogService = [];
		// disable selection if there are different SEAT/BED selected offered service in same train
		const canSelectSeatForNodes = selectedSeatOffersData.value.reduce((carry, offeredService) => {
			if(_has(carry, offeredService.nodeId)){
				if(carry[offeredService.nodeId].catalogServiceId != offeredService.catalogServiceId){
					hasDifferentCatalogService.push(offeredService.nodeId);
				}
				carry[offeredService.nodeId].canSelectSeatmap = Boolean(carry[offeredService.nodeId].canSelectSeatmap && offeredService.can_select_seatmap);
			}else{
				carry[offeredService.nodeId] = {
					catalogServiceId: offeredService.catalogServiceId,
					canSelectSeatmap: Boolean(_get(carry, offeredService.nodeId, true) && offeredService.can_select_seatmap)
				};
			}
			return carry;
		}, {});

		if(hasDifferentCatalogService.length > 0) return false;
		// true if at least one node has seat selection

		const resp = Object.entries(canSelectSeatForNodes).some(([nodeId, item]) => item.canSelectSeatmap);
		
		return resp;
	});

	const travellersDataOfferRequired = computed(() => {
		const travellersData = {};

		if(selectedOffersData.value){
			for (const selectedOffer of selectedOffersData.value) {
				const catalogService = _get(booking.servicesMap, ['catalog_services', selectedOffer.catalogServiceId]);
				const offerService = _get(booking.servicesMap, ['offers', selectedOffer.offerId]);
				if(offerService && offerService.selection_traveller_parameters_map){
					if(!travellersData[selectedOffer.travellerXmlId]) travellersData[selectedOffer.travellerXmlId] = [];
					const params = Object.keys(offerService.selection_traveller_parameters_map);
					if(params) travellersData[selectedOffer.travellerXmlId].push(...params);
				}
			}
		}

		return travellersData;
	});

	async function fetchForCustomizeOffers(){
		// if just loaded, do nothing
		if(travelSolutionView.value && travelSolutionView.value.offers_map) return;
		if(!travelSolutionView.value.original || booking.passengers.length === 0) {
			booking.reset();
			stepper.goBack();
		}

		return axios.post('trenitalia/get-offers', {
			travelSolutions: [travelSolutionView.value.original],
			travellers: booking.passengers.map(p => p.data)
		}).then((response) => {
			booking.updateServicesMap(response.data.servicesMap || response.data.serviceMap);
			travelSolutionView.value = response.data.travelSolutions[0];
		});
	}

	function initCustomizedOffers(){
		const services = _get(travelSolutionView.value, 'offers_map');
		if(!services) return;
		const selectedCustomizedOffers = checkAndSetIfArePreSelectedOffersAndServices(travelSolutionView.value);

		if (Object.keys(selectedCustomizedOffers).length <= 0) {
			for (const train of travelSolutionView.value.trains) {
				if(!services[train.id] || !services[train.id].travellers) continue;
				for (const passenger of booking.passengers) {
					if(!services[train.id].travellers[passenger.data.xmlId] || services[train.id].travellers[passenger.data.xmlId].length == 0) continue;
					for (const catalogService of services[train.id].travellers[passenger.data.xmlId]) {	
						const offer = catalogService.offers.find(s => {
							return s.selectable;
						});
						if(!offer) continue;
						if(!selectedCustomizedOffers[train.id]) selectedCustomizedOffers[train.id] = {};
						const offers = offer.offered || offer.offers;
						const offeredService = travelSolutionView.value.offeredServices[offers[0]];
						selectedCustomizedOffers[train.id][passenger.data.xmlId] = makeCustomizedOfferedServiceSelection(offeredService, booking.servicesMap);
						break;
					}
				}
			}
		}
		
		state.selectedCustomizedOffers = selectedCustomizedOffers;
	}

	function initTrainGridOffers(){
		// init with default selected prices
		const selectedTrainGridOffers = {};
		if (_get(travelSolutionView.value, 'priceGrid')) {
			for (const trainId in travelSolutionView.value.priceGrid) {
				if (!Object.prototype.hasOwnProperty.call(travelSolutionView.value.priceGrid, trainId)) continue;
				const trainPriceGrid = travelSolutionView.value.priceGrid[trainId];
				if (trainPriceGrid.best_price && trainPriceGrid.best_price.row_key && trainPriceGrid.best_price.col_key) {
					selectedTrainGridOffers[trainId] = {
						offerId: trainPriceGrid.best_price.row_key,
						catalogServiceId: trainPriceGrid.best_price.col_key,
						offers: trainPriceGrid.best_price.offers,
						totalPrice: trainPriceGrid.best_price.price
					};
				}
			}
		}
		state.selectedTrainGridOffers = selectedTrainGridOffers;
	}

	function setSelectedCustomizedOffer(trainId, travellerId, offerId, catalogServiceId, offered){
		if(!state.selectedCustomizedOffers) state.selectedCustomizedOffers = [];
		if(!state.selectedCustomizedOffers[trainId]) state.selectedCustomizedOffers[trainId] = {};
		// if(!this.selectedCustomizedOffers[trainId]) this.selectedCustomizedOffers[props.trainId] = {};
		const offeredService = travelSolutionView.value.offeredServices[offered[0]];
		state.selectedCustomizedOffers[trainId][travellerId] = makeCustomizedOfferedServiceSelection(offeredService, booking.servicesMap);
	}

	function setSelectedTrainGridOffer(trainId, offerId, catalogServiceId){
		const priceSelected = travelSolutionView.value.priceGrid[trainId].matrix[offerId][catalogServiceId];
		state.selectedTrainGridOffers[trainId] = {
			offerId, 
			catalogServiceId,
			offers: priceSelected.offers,
			totalPrice: priceSelected.totalPrice
		};
	}

	function setSelectedSolution(solution=null){
		if(travelSolutionView.value == solution) return; // nothing changed
		Object.assign(state, initialState); // reset selection, keep
		travelSolutionView.value = solution;
		// unselect
		if(!booking.isPostSale && stepper.inTravelSolutionsPage) stepper.stepStatus[stepper.step].completed = solution ? true : false; // still need or deprecated?
	}

	async function evaluateAndGoNext(travelSolution = null){
		const stepper = useBookingStepperStore();
		// const i18n = useI18n();

		if (booking.isEvaluatedValid) {
			stepper.goNext();
			return;
		}

		stepper.nextLoading = true;
		if (postSale.reopenedTravel) {
			return postSale.evaluateTravel()
			.then(res => {
				if (!res) return;
				if (postSale.isValidEvaluate) {
					stepper.goNext();
				} else {
					dialogManagerStore.openSimpleMessageDialog({
						title: 'Selezione non disponibile',
						text: 'Non è più possibile acquistare il prezzo selezionato.'
					});
				}
			}).catch(e => {
				dialogManagerStore.showErrorDialogFromError(e);
				console.error('next getAvailability', e);
			});
		} else {
			if (!travelSolution) travelSolution = travelSolutionView.value;
			return booking.getAvailability({
				travelSolutions: travelSolution.original,
				selectedOffer: selectedOffersExtended.value,
				withSeatsSelection: booking.requestSeatSelection,
				isReturn
			}).then(async res => {
				if (!res) return;
				if (booking.requestSeatSelection && !_get(booking.trainSeatMaps, 'leastOneSeatSelection')){
					await dialogManagerStore.openSimpleMessageDialog({
						title: 'Attenzione',
						// title: this.$i18n.t('dialogs.warning', 'Attenzione'),
						text: 'Per l\'offerta selezionata non è possibile selezionare i posti a sedere.'
					});
					booking.setRequestSeatSelection(false);
					return;
				}

				stepper.goNext();
			}).catch(e => {
				dialogManagerStore.showErrorDialogFromError(e);
				console.error('next getAvailability', e);
			}).then(() => {
				stepper.nextLoading = false;
			});
		}
	}

	watch(travelSolutionView, (newValue) => {
		booking.setSeatSelection({}, isReturn);
		booking.setEvaluatedSolution(null, isReturn);
		booking.setRequestSeatSelection(false)

		if(newValue){
			if(!isCarnet){
				setTimeout(() => {
					initTrainGridOffers();
				}, state.useCustomizedOffers ? 100 : 0);
				setTimeout(() => {
					initCustomizedOffers();
				}, state.useCustomizedOffers ? 0 : 100);
			}

			if (newValue.travellers && !booking.isPostSale) booking.resetTravellers(newValue.travellers);
		}
	}, {
		immediate: true
	});

	watch(canRequestSeatSelection, () => {
		if(!canRequestSeatSelection.value){
			booking.setRequestSeatSelection(false);
		}
	});

	const exposedData = {
		state,
		selectedOffers,
		selectedOffersData,
		selectedSeatOffersData,
		selectedOffersExtended,
		canRequestSeatSelection,
		travellersDataOfferRequired,
		fetchForCustomizeOffers,
		initCustomizedOffers,
		setSelectedCustomizedOffer,
		setSelectedTrainGridOffer,
		setSelectedSolution,
		evaluateAndGoNext,
		travelSolutionView: readonly(travelSolutionView)
	};

	provide('travelSolutionSelection', exposedData);

	return exposedData;
}